import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Lightbulb,
  UserPlus,
  MapPinned,
  User,
  Heart,
  Baby,
  Users,
  Calendar,
  Mail,
  Phone,
  UserCircle2,
  Globe,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/CustomTrip.css';

// Hand-drawn squiggly underline used below each question title
const SquigglyUnderline = () => (
  <svg
    className="title-underline"
    viewBox="0 0 320 14"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <path
      d="M2 9 Q 40 2, 80 8 T 160 7 T 240 8 T 318 6"
      stroke="currentColor"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Simple 12-month picker (no year). Multi-select.
 * Stores month numbers as comma-separated string ("06,07,09").
 */
const MonthPicker = ({ value, onChange, monthLabels }) => {
  const selected = value ? String(value).split(',').filter(Boolean) : [];

  const toggle = (key) => {
    const next = selected.includes(key)
      ? selected.filter((m) => m !== key)
      : [...selected, key];
    onChange(next.join(','));
  };

  return (
    <div className="ct2-month-picker" data-testid="ct2-month-picker">
      <div className="ct2-month-grid">
        {monthLabels.map((label, idx) => {
          const key = String(idx + 1).padStart(2, '0');
          const isSelected = selected.includes(key);
          return (
            <button
              key={key}
              type="button"
              className={`ct2-month-cell ${isSelected ? 'is-selected' : ''}`}
              onClick={() => toggle(key)}
              data-testid={`ct2-month-${key}`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TOTAL_STEPS = 10;
const BUDGET_CHIPS = ['2000', '3000', '4000', '5000', '7500', '10000'];
const ACTIVITY_KEYS = [
  'hiking', 'canyoning', 'diving', 'whales', 'jeep', 'paragliding',
  'fishing', 'biking', 'swimming', 'horse', 'buggy', 'desertas',
  'surf', 'sightseeing', 'cultural', 'gastronomy',
];
const PROJECT_MIN_CHARS = 10;
const PROJECT_MAX_CHARS = 1000;

const CustomTrip = () => {
  const { t, language } = useLanguage();

  const [currentStep, setCurrentStep] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [formData, setFormData] = useState({
    projectStage: '',
    travelGroup: '',
    adults: 2,
    children: 0,
    childrenAges: [],
    knowDates: '',
    travelDates: '',
    travelMonths: '',
    durationChoice: '',
    durationOther: '',
    interests: [],
    hasBudget: '',
    budgetAmount: '',
    projectDescription: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
  });

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const set = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const toggleInterest = (key) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(key)
        ? prev.interests.filter((i) => i !== key)
        : [...prev.interests, key],
    }));
  };

  // Step 8 (budget amount) is skipped when user answers "no" on step 7
  const shouldSkipBudgetAmount = formData.hasBudget === 'no';

  const goTo = (target) => {
    if (target >= 1 && target <= TOTAL_STEPS) {
      setCurrentStep(target);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const nextStep = () => {
    if (currentStep === 8 && shouldSkipBudgetAmount) return goTo(9);
    goTo(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep === 10 && shouldSkipBudgetAmount) return goTo(7);
    goTo(currentStep - 1);
  };

  const projectDescLen = formData.projectDescription.length;

  const isStepValid = useMemo(() => {
    switch (currentStep) {
      case 1:
        return formData.projectStage !== '';
      case 2:
        return formData.travelGroup !== '';
      case 3:
        return formData.adults > 0;
      case 4:
        return formData.knowDates !== '';
      case 5: {
        const hasDuration =
          formData.durationChoice !== '' &&
          (formData.durationChoice !== 'more' || formData.durationOther.trim() !== '');
        if (!hasDuration) return false;
        if (formData.knowDates === 'yes') {
          return formData.travelDates !== '';
        }
        // knowDates === 'no'
        return formData.travelMonths !== '';
      }
      case 6:
        return formData.interests.length > 0;
      case 7:
        return formData.hasBudget !== '';
      case 8:
        return formData.budgetAmount !== '' && Number(formData.budgetAmount) > 0;
      case 9:
        return (
          projectDescLen >= PROJECT_MIN_CHARS && projectDescLen <= PROJECT_MAX_CHARS
        );
      case 10:
        return (
          formData.firstName.trim() !== '' &&
          formData.lastName.trim() !== '' &&
          formData.email.trim() !== '' &&
          formData.phone.trim() !== ''
        );
      default:
        return false;
    }
  }, [currentStep, formData, projectDescLen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !isStepValid) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const endpoint = apiBase
        ? `${apiBase}/api/custom-trip/submit`   // dev: FastAPI (Emergent preview)
        : '/api/custom-trip.php';               // prod: PHP on Hostinger
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail || 'Submission failed. Please try again.');
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Visible progress numbers (skipping budget-amount step when not relevant)
  const visibleTotal = shouldSkipBudgetAmount ? TOTAL_STEPS - 1 : TOTAL_STEPS;
  const visibleStep =
    shouldSkipBudgetAmount && currentStep >= 9 ? currentStep - 1 : currentStep;

  const projectStageOptions = [
    { value: 'research', label: t('ct2.stageResearch'), icon: Lightbulb },
    { value: 'planning', label: t('ct2.stagePlanning'), icon: UserPlus },
    { value: 'booking', label: t('ct2.stageBooking'), icon: MapPinned },
  ];
  const travelGroupOptions = [
    { value: 'alone', label: t('ct2.groupAlone'), icon: User },
    { value: 'couple', label: t('ct2.groupCouple'), icon: Heart },
    { value: 'family', label: t('ct2.groupFamily'), icon: Baby },
    { value: 'group', label: t('ct2.groupGroup'), icon: Users },
  ];
  const yesNoOptions = [
    { value: 'yes', label: t('ct2.yes') },
    { value: 'no', label: t('ct2.no') },
  ];
  const monthLabels = useMemo(() => {
    const locale = { fr: 'fr-FR', en: 'en-US', pt: 'pt-PT', de: 'de-DE' }[language] || 'en-US';
    const fmt = new Intl.DateTimeFormat(locale, { month: 'short' });
    return Array.from({ length: 12 }, (_, i) =>
      fmt.format(new Date(2024, i, 1)).replace('.', '')
    );
  }, [language]);

  const durationChips = [
    { value: 'lt5', label: t('ct2.durationLessThan5') },
    { value: '5', label: t('ct2.duration5') },
    { value: '8', label: t('ct2.duration8') },
    { value: '15', label: t('ct2.duration15') },
    { value: 'more', label: t('ct2.durationMore') },
  ];

  return (
    <div className="ct2-page" data-testid="custom-trip-page">
      <section className="ct2-hero">
        <div className="ct2-hero-overlay" />
        <div className="container">
          <div className={`ct2-hero-content ${isVisible ? 'animated fadeIn' : 'animated'}`}>
            <h1 className="display-medium">{t('ct2.heroTitle')}</h1>
            <p className="body-large">{t('ct2.heroSubtitle')}</p>
          </div>
        </div>
      </section>

      <div className="ct2-progress-wrapper">
        <div className="container">
          <div className="ct2-progress">
            <div className="ct2-progress-bar">
              <div
                className="ct2-progress-fill"
                style={{ width: `${(visibleStep / visibleTotal) * 100}%` }}
              />
            </div>
            <div className="ct2-progress-text" data-testid="ct2-progress-text">
              {t('ct2.progress', { step: visibleStep, total: visibleTotal })}
            </div>
          </div>
        </div>
      </div>

      <section className="ct2-section">
        <div className="container">
          {isSubmitted ? (
            <div className="ct2-success" data-testid="custom-trip-success">
              <CheckCircle size={72} />
              <h2>{t('ct2.successTitle', { name: formData.firstName || '🙂' })}</h2>
              <p>{t('ct2.successText', { email: formData.email })}</p>
              <a href="/" className="ct2-btn ct2-btn-primary" data-testid="ct2-back-home">
                {t('ct2.successCta')}
              </a>
            </div>
          ) : (
            <form className="ct2-form" onSubmit={handleSubmit} data-testid="custom-trip-form">
              {/* Step 1 */}
              {currentStep === 1 && (
                <div className="ct2-step" data-testid="ct2-step-1">
                  <h2 className="ct2-title">{t('ct2.step1Title')}</h2>
                  <SquigglyUnderline />
                  <div className="ct2-options ct2-options-stack">
                    {projectStageOptions.map((opt) => {
                      const Icon = opt.icon;
                      const selected = formData.projectStage === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`ct2-option ${selected ? 'is-selected' : ''}`}
                          onClick={() => set('projectStage', opt.value)}
                          data-testid={`ct2-projectstage-${opt.value}`}
                        >
                          <Icon size={28} strokeWidth={1.6} className="ct2-option-icon" />
                          <span className="ct2-option-label">{opt.label}</span>
                          <span className={`ct2-radio ${selected ? 'is-selected' : ''}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 2 */}
              {currentStep === 2 && (
                <div className="ct2-step" data-testid="ct2-step-2">
                  <h2 className="ct2-title">{t('ct2.step2Title')}</h2>
                  <SquigglyUnderline />
                  <div className="ct2-options ct2-options-grid-2">
                    {travelGroupOptions.map((opt) => {
                      const Icon = opt.icon;
                      const selected = formData.travelGroup === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`ct2-option ${selected ? 'is-selected' : ''}`}
                          onClick={() => set('travelGroup', opt.value)}
                          data-testid={`ct2-travelgroup-${opt.value}`}
                        >
                          <Icon size={28} strokeWidth={1.6} className="ct2-option-icon" />
                          <span className="ct2-option-label">{opt.label}</span>
                          <span className={`ct2-radio ${selected ? 'is-selected' : ''}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 3 */}
              {currentStep === 3 && (
                <div className="ct2-step" data-testid="ct2-step-3">
                  <div className="ct2-info">
                    <strong>{t('ct2.step3InfoTitle')}</strong>
                      <p>{t('ct2.step3InfoText')}</p>
                  </div>
                  <h2 className="ct2-title">{t('ct2.step3Title')}</h2>
                  <SquigglyUnderline />
                  <div className="ct2-counter-list">
                    <div className="ct2-counter">
                      <label>{t('ct2.adults')}:</label>
                      <div className="ct2-counter-controls">
                        <button
                          type="button"
                          className="ct2-counter-btn"
                          onClick={() => set('adults', Math.max(1, formData.adults - 1))}
                          data-testid="ct2-adults-minus"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          className="ct2-counter-value"
                          value={formData.adults}
                          readOnly
                          data-testid="ct2-adults-value"
                        />
                        <button
                          type="button"
                          className="ct2-counter-btn"
                          onClick={() => set('adults', formData.adults + 1)}
                          data-testid="ct2-adults-plus"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="ct2-counter">
                      <label>
                        {t('ct2.children')}:
                        <span className="ct2-counter-hint">{t('ct2.childrenHint')}</span>
                      </label>
                      <div className="ct2-counter-controls">
                        <button
                          type="button"
                          className="ct2-counter-btn"
                          onClick={() => set('children', Math.max(0, formData.children - 1))}
                          data-testid="ct2-children-minus"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          className="ct2-counter-value"
                          value={formData.children}
                          readOnly
                          data-testid="ct2-children-value"
                        />
                        <button
                          type="button"
                          className="ct2-counter-btn"
                          onClick={() => set('children', formData.children + 1)}
                          data-testid="ct2-children-plus"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4 */}
              {currentStep === 4 && (
                <div className="ct2-step" data-testid="ct2-step-4">
                  <div className="ct2-info">
                    <strong>{t('ct2.step4InfoTitle')}</strong>
                      <p>{t('ct2.step4InfoText')}</p>
                  </div>
                  <h2 className="ct2-title">{t('ct2.step4Title')}</h2>
                  <SquigglyUnderline />
                  <div className="ct2-options ct2-options-grid-2">
                    {yesNoOptions.map((opt) => {
                      const selected = formData.knowDates === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`ct2-option ct2-option-yesno ${selected ? 'is-selected' : ''}`}
                          onClick={() => set('knowDates', opt.value)}
                          data-testid={`ct2-knowdates-${opt.value}`}
                        >
                          <span className="ct2-option-label">{opt.label}</span>
                          <span className={`ct2-radio ${selected ? 'is-selected' : ''}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 5 — date + duration */}
              {currentStep === 5 && (
                <div className="ct2-step" data-testid="ct2-step-5">
                  <div className="ct2-info">
                    <strong>{t('ct2.step5InfoTitle')}</strong>
                    <p>{t('ct2.step5InfoText')}</p>
                  </div>

                  {/* Title + date picker — only when user knows the dates */}
                  {formData.knowDates === 'yes' && (
                    <>
                      <h2 className="ct2-title">{t('ct2.step5Title')}</h2>
                      <SquigglyUnderline />
                      <div className="ct2-date-wrapper" onClick={(e) => {
                        const input = e.currentTarget.querySelector('input');
                        if (input && typeof input.showPicker === 'function') {
                          try { input.showPicker(); } catch (_) { /* noop */ }
                        }
                      }}>
                        <Calendar size={20} className="ct2-date-icon" strokeWidth={1.6} />
                        <input
                          type="date"
                          className="ct2-date-input"
                          min={new Date().toISOString().split('T')[0]}
                          value={formData.travelDates}
                          onChange={(e) => set('travelDates', e.target.value)}
                          data-testid="ct2-travel-dates"
                        />
                      </div>
                    </>
                  )}

                  {/* Duration always shown */}
                  <h3 className="ct2-subtitle-mini" style={formData.knowDates === 'no' ? { marginTop: 0 } : undefined}>
                    {t('ct2.step5DurationLabel')}
                  </h3>
                  <SquigglyUnderline />
                  <div className="ct2-chips">
                    {durationChips.map((d) => (
                      <button
                        key={d.value}
                        type="button"
                        className={`ct2-chip ${
                          formData.durationChoice === d.value ? 'is-selected' : ''
                        }`}
                        onClick={() => set('durationChoice', d.value)}
                        data-testid={`ct2-duration-${d.value}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  {formData.durationChoice === 'more' && (
                    <div className="ct2-budget-custom" style={{ marginTop: 18 }}>
                      <input
                        type="text"
                        value={formData.durationOther}
                        onChange={(e) => set('durationOther', e.target.value)}
                        placeholder={t('ct2.durationOtherPlaceholder')}
                        data-testid="ct2-duration-other"
                      />
                    </div>
                  )}

                  {/* Month multi-select — only when user does NOT know the dates AND chose a duration */}
                  {formData.knowDates === 'no' && formData.durationChoice !== '' && (
                    <>
                      <h3 className="ct2-subtitle-mini">{t('ct2.monthsHeading')}</h3>
                      <SquigglyUnderline />
                      <p className="ct2-subtitle" style={{ marginTop: -32 }}>
                        {t('ct2.monthsMultiHint')}
                      </p>
                      <MonthPicker
                        value={formData.travelMonths}
                        onChange={(v) => set('travelMonths', v)}
                        monthLabels={monthLabels}
                      />
                    </>
                  )}
                </div>
              )}

              {/* Step 6 — activities (multi-select) */}
              {currentStep === 6 && (
                <div className="ct2-step" data-testid="ct2-step-6">
                  <h2 className="ct2-title">{t('ct2.step6ActivitiesTitle')}</h2>
                  <SquigglyUnderline />
                  <p className="ct2-subtitle" style={{ marginTop: -32 }}>
                    {t('ct2.step6ActivitiesHint')}
                  </p>
                  <div className="ct2-activities-grid">
                    {ACTIVITY_KEYS.map((key) => {
                      const selected = formData.interests.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`ct2-activity ${selected ? 'is-selected' : ''}`}
                          onClick={() => toggleInterest(key)}
                          data-testid={`ct2-activity-${key}`}
                        >
                          <span className="ct2-activity-label">{t(`ct2.activities.${key}`)}</span>
                          <span className={`ct2-check ${selected ? 'is-selected' : ''}`}>
                            {selected ? '✓' : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 7 — has budget */}
              {currentStep === 7 && (
                <div className="ct2-step" data-testid="ct2-step-7">
                  <div className="ct2-info">
                    <strong>{t('ct2.step6InfoTitle')}</strong>
                      <p>{t('ct2.step6InfoText')}</p>
                  </div>
                  <h2 className="ct2-title">{t('ct2.step6Title')}</h2>
                  <SquigglyUnderline />
                  <div className="ct2-options ct2-options-grid-2">
                    {yesNoOptions.map((opt) => {
                      const selected = formData.hasBudget === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`ct2-option ct2-option-yesno ${selected ? 'is-selected' : ''}`}
                          onClick={() => set('hasBudget', opt.value)}
                          data-testid={`ct2-hasbudget-${opt.value}`}
                        >
                          <span className="ct2-option-label">{opt.label}</span>
                          <span className={`ct2-radio ${selected ? 'is-selected' : ''}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 7 — budget amount */}
              {currentStep === 8 && (
                <div className="ct2-step" data-testid="ct2-step-8">
                  <div className="ct2-info">
                    <p>{t('ct2.step7InfoText')}</p>
                  </div>
                  <h2 className="ct2-title">{t('ct2.step7Title')}</h2>
                  <SquigglyUnderline />
                  <div className="ct2-chips">
                    {BUDGET_CHIPS.map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        className={`ct2-chip ${
                          formData.budgetAmount === amount ? 'is-selected' : ''
                        }`}
                        onClick={() => set('budgetAmount', amount)}
                        data-testid={`ct2-budget-${amount}`}
                      >
                        €{Number(amount).toLocaleString('en-US')}
                      </button>
                    ))}
                  </div>
                  <div className="ct2-or">{t('ct2.step7Or')}</div>
                  <div className="ct2-budget-custom">
                    <input
                      type="number"
                      min="0"
                      placeholder={t('ct2.step7CustomPlaceholder')}
                      value={
                        BUDGET_CHIPS.includes(formData.budgetAmount) ? '' : formData.budgetAmount
                      }
                      onChange={(e) => set('budgetAmount', e.target.value)}
                      data-testid="ct2-budget-custom"
                    />
                  </div>
                </div>
              )}

              {/* Step 8 — project description */}
              {currentStep === 9 && (
                <div className="ct2-step" data-testid="ct2-step-9">
                  <div className="ct2-info">
                    <strong>{t('ct2.step8InfoTitle')}</strong>
                      <p>{t('ct2.step8InfoText')}</p>
                  </div>
                  <h2 className="ct2-title">{t('ct2.step8Title')}</h2>
                  <SquigglyUnderline />
                  <textarea
                    className="ct2-textarea"
                    placeholder={t('ct2.step8Placeholder')}
                    maxLength={PROJECT_MAX_CHARS}
                    value={formData.projectDescription}
                    onChange={(e) => set('projectDescription', e.target.value)}
                    data-testid="ct2-project-description"
                  />
                  <span
                    className={`ct2-textarea-counter ${
                      projectDescLen > 0 && projectDescLen < PROJECT_MIN_CHARS ? 'is-invalid' : ''
                    }`}
                  >
                    {t('ct2.step8CharsCounter', {
                      count: projectDescLen,
                      min: PROJECT_MIN_CHARS,
                    })}
                  </span>
                </div>
              )}

              {/* Step 9 — contact */}
              {currentStep === 10 && (
                <div className="ct2-step" data-testid="ct2-step-10">
                  <div className="ct2-info">
                    <strong>{t('ct2.step9InfoTitle')}</strong>
                      <p>{t('ct2.step9InfoText')}</p>
                  </div>
                  <h2 className="ct2-title">{t('ct2.step9Title')}</h2>
                  <SquigglyUnderline />
                  <div className="ct2-contact-grid">
                    <div className="ct2-field">
                      <label>
                        <UserCircle2 size={18} strokeWidth={1.6} /> {t('ct2.firstName')} *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => set('firstName', e.target.value)}
                        data-testid="ct2-firstName"
                      />
                    </div>
                    <div className="ct2-field">
                      <label>{t('ct2.lastName')} *</label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => set('lastName', e.target.value)}
                        data-testid="ct2-lastName"
                      />
                    </div>
                    <div className="ct2-field ct2-field-full">
                      <label>
                        <Mail size={18} strokeWidth={1.6} /> {t('ct2.email')} *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => set('email', e.target.value)}
                        data-testid="ct2-email"
                      />
                    </div>
                    <div className="ct2-field">
                      <label>
                        <Phone size={18} strokeWidth={1.6} /> {t('ct2.phone')} *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => set('phone', e.target.value)}
                        data-testid="ct2-phone"
                      />
                    </div>
                    <div className="ct2-field">
                      <label>
                        <Globe size={18} strokeWidth={1.6} /> {t('ct2.country')}
                      </label>
                      <input
                        type="text"
                        value={formData.country}
                        onChange={(e) => set('country', e.target.value)}
                        data-testid="ct2-country"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="ct2-nav">
                {currentStep > 1 && (
                  <button
                    type="button"
                    className="ct2-btn ct2-btn-secondary"
                    onClick={prevStep}
                    data-testid="ct2-prev"
                  >
                    <ArrowLeft size={18} /> {t('ct2.previous')}
                  </button>
                )}
                {currentStep < TOTAL_STEPS ? (
                  <button
                    type="button"
                    className="ct2-btn ct2-btn-primary"
                    onClick={nextStep}
                    disabled={!isStepValid}
                    data-testid="ct2-next"
                  >
                    {t('ct2.next')} <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="ct2-btn ct2-btn-primary"
                    disabled={!isStepValid || isSubmitting}
                    data-testid="custom-trip-submit"
                  >
                    {isSubmitting ? t('ct2.sending') : t('ct2.submit')} <CheckCircle size={18} />
                  </button>
                )}
              </div>

              {submitError && (
                <p className="ct2-error" data-testid="custom-trip-error">
                  {submitError}
                </p>
              )}
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default CustomTrip;
